import React, { useEffect, useRef, useState } from 'react';
import { Progress } from 'reactstrap';
import './FloatingNotifications.scss';

export const FloatingNotifications = ({
  visible,
  onDismiss,
  fileName,
  totalRegisters,
  successCount,
  failedCount,
  completed,
  lastError,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Snapshots para evitar mostrar 0/0 cuando se preserva el resumen y el hook resetea contadores
  const [snapshotSuccess, setSnapshotSuccess] = useState(0);
  const [snapshotFailed, setSnapshotFailed] = useState(0);
  const prevCompletedRef = useRef(completed);
  const prevVisibleRef = useRef(visible);

  // Reset de snapshots al iniciar nueva campaña (completed -> false) o al abrir visible con campaña en curso
  useEffect(() => {
    const prevCompleted = prevCompletedRef.current;
    const prevVisible = prevVisibleRef.current;
    if ((prevCompleted && !completed) || (!prevVisible && visible && !completed)) {
      setSnapshotSuccess(0);
      setSnapshotFailed(0);
    }
    prevCompletedRef.current = completed;
    prevVisibleRef.current = visible;
  }, [completed, visible]);

  // Actualizar snapshots cuando completamos o cuando aumentan los contadores
  useEffect(() => {
    if (completed) {
      setSnapshotSuccess((prev) => Math.max(prev, successCount || 0));
      setSnapshotFailed((prev) => Math.max(prev, failedCount || 0));
    } else {
      // Durante el procesamiento, mantener el máximo observado (por si hay pequeños resets temporales)
      setSnapshotSuccess((prev) => Math.max(prev, successCount || 0));
      setSnapshotFailed((prev) => Math.max(prev, failedCount || 0));
    }
  }, [successCount, failedCount, completed]);

  const currentCount = successCount + failedCount;
  let progress = 0;

  if (totalRegisters && totalRegisters > 0) {
    const base = Math.round((currentCount / totalRegisters) * 100);
    progress = Math.min(100, Math.max(0, base));
  }

  if (completed) {
    progress = 100;
  }

  if (!visible) {
    return null;
  }

  const isFailure = completed && !!lastError;

  let statusText = 'Preparando envío...';
  if (completed && !isFailure) {
    const total = totalRegisters || currentCount || 0;
    statusText = `${total} links de pago enviados`;
  } else if (isFailure) {
    statusText = 'Envío de links de pago fallidos';
  } else if (totalRegisters > 0) {
    statusText = `Enviando ${totalRegisters} links de pago...`;
  }

  let bodySubtitle = 'Esperando notificaciones...';
  if (completed && !isFailure) {
    bodySubtitle = 'Envío completado.';
  } else if (isFailure) {
    bodySubtitle = 'Envío fallido.';
  } else if (totalRegisters > 0) {
    bodySubtitle = `Enviando ${currentCount} de ${totalRegisters}...`;
  }

  const headerClass = isFailure ? 'bg-danger' : (completed ? 'bg-success' : 'bg-processing');
  const displayedSuccess = completed ? Math.max(successCount, snapshotSuccess) : successCount;
  const displayedFailed = completed ? Math.max(failedCount, snapshotFailed) : failedCount;
  const truncate = (text, max = 50) => {
    if (!text) return '';
    const str = String(text);
    return str.length > max ? `${str.slice(0, max)}…` : str;
  };

  return (
    <div className="floating-notification floating-notification-container">
      <div className="floating-notification-dialog">
        <div className={`floating-notification-header ${headerClass}`}>
          <span>
            <i className="mdi mdi-bell-ring text-white mr-1" />
            <strong>{statusText}</strong>
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setIsCollapsed(!isCollapsed)}>
              <i className={`mdi mdi-chevron-${isCollapsed ? 'up' : 'down'}`} />
            </button>
            <button onClick={onDismiss}>
              <i className="mdi mdi-close text-white" />
            </button>
          </div>
        </div>

        <div className={`floating-notification-body ${isCollapsed ? 'collapsed' : ''}`}>
          <div className="floating-notification-body-title">
            <span title={fileName || 'Campaña en curso'}>
              {truncate(fileName) || 'Campaña en curso'}
            </span>
          </div>
          <div className="floating-notification-body-subtitle">{bodySubtitle}</div>
          <div
            className="floating-notification-body-metrics d-flex flex-wrap align-items-center mt-2"
            style={{ gap: '12px' }}
          >
            <span>
              Exitosos: <strong>{displayedSuccess}</strong>
            </span>
            <span>
              Fallidos: <strong className="text-danger">{displayedFailed}</strong>
            </span>
          </div>
        </div>

        <div className={`floating-notification-footer ${isCollapsed ? 'collapsed' : ''}`}>
          <Progress value={progress} color="primary" style={{ height: '8px' }} />
          <div className="floating-notification-footer-progress-text">
            {progress}%
          </div>
        </div>

        {/* Sin mensajes adicionales en estado de error; solo la ventana flotante */}
      </div>
    </div>
  );
};

